/**
 * The learner's workspace root (issues #16, #465 part 1a).
 *
 * ONE authenticated read backs this Home page:
 * `GET /api/lms/workspaces/learning/today`. The workspace owns cross-course
 * reads that no single course endpoint can answer, which is a projection rather
 * than a page, and looping the per-course endpoint to build it would be N round
 * trips and N chances for N clocks.
 *
 * This file owns identity, the greeting, and the shell. Everything below the
 * heading is `SeatTodayView`, a composition of widgets over the projection that
 * decides nothing: the ranked next step, the course collection, coursework,
 * releases, and standing. The server ranked one act across the whole seat,
 * owns its label, and named the course it belongs to.
 *
 * `/dashboard/student` is still served and is unchanged. Reverting this file
 * restores the shipped dashboard; that is the whole rollback.
 */
import { SeatAllClassesLink } from "@/components/workspace/SeatAllClassesLink";
import { SeatTodayView, SeatTodayViewSkeleton } from "@/components/workspace/SeatTodayView";
import { useAuth } from "@/contexts/AuthContext";
import { useSeatWorkspace } from "@/hooks/use-seat-workspace";
import { ApiError } from "@/lib/api/client";
import { greetingSuffix } from "@/lib/greeting";
import { loadState } from "@/lib/query-state";
import { DashboardNotice } from "@/components/dashboard/DashboardNotice";

export function StudentDashboardPage() {
  const { me } = useAuth();
  const query = useSeatWorkspace("learning");
  const { refetch } = query;
  const authorityRefused = query.error instanceof ApiError && (query.error.status === 401 || query.error.status === 403);
  const data = !authorityRefused && query.data?.perspective === "learning" ? query.data : undefined;
  const state = authorityRefused ? "error" : loadState(query);
  // The endpoint answers one non-disclosing 404 for every seat the caller does
  // not hold, and the hook latches it rather than retrying. A retry button is a
  // lie against a settled answer, so this case gets the registrar, not a reload.
  // ONLY the 404. A 401 is a credential mid-refresh and a 403 is a membership
  // that is inactive or unresolved; both recover, so they keep the retryable
  // error surface below rather than claiming there is no record.
  const noSeat = query.isNoSeat || (
    query.error instanceof ApiError && query.error.status === 404
  );
  const greeting = greetingSuffix(me?.honorific, me?.preferred_name, me?.full_name);
  const term = data?.active_terms.length === 1
    ? data.active_terms[0].name
    : data && data.active_terms.length > 1
      ? `${data.active_terms.length} active terms`
      : data?.term.state === "ready" ? data.term.value.name : null;

  return (
    <div className="app-page min-w-0">
      {/* The page frame, the same as the teaching Home: the term as the
          eyebrow, the greeting as the page object, and the one way out to the
          session list. No meta line: no line above the widgets restates what
          they show. */}
      <header data-slot="page-frame" className="page-heading">
        <div className="min-w-0">
          {/* The term only, as on the teaching Home: the shell already names
              the institution and the workspace. */}
          {term && <div className="eyebrow">{term}</div>}
          <h1 className="text-foreground">As-salāmu ʿalaykum{greeting}</h1>
        </div>
        {/* The same control, in the same place, as the teaching Home: the class
            schedule no longer depends on whether a next step was ranked. */}
        <SeatAllClassesLink basePath="/student" />
      </header>

      {!data && noSeat && <DashboardNotice state="absent" onRetry={() => void refetch()} />}

      {!data && !noSeat && state === "error" && <DashboardNotice state="error" onRetry={() => void refetch()} />}
      {!data && !noSeat && (state === "loading" || state === "paused") && <SeatTodayViewSkeleton />}

      {data && <SeatTodayView key={`${me?.tenant_id}:${me?.user_id}`} stale={query.isError} projection={data} onRetry={() => void refetch()} />}
    </div>
  );
}
